import {SVGIcon} from 'types';

import {useEntityAccess} from 'core/hooks/useEntityAccess';

import AiAssistIcon from 'components/icons/aiAssist.svg';
import ProducersIcon from 'components/icons/metrics/producers.svg';
import AccountExecutivesIcon from 'components/icons/metrics/accountExecutives.svg';
import AccountManagersIcon from 'components/icons/metrics/managers.svg';
import IndustriesIcon from 'components/icons/metrics/industries.svg';
import AccountsIcon from 'components/icons/metrics/accounts.svg';
import ActivitiesIcon from 'components/icons/activities.svg';
import RenewalsIcon from 'components/icons/links/renewals.svg';
import OpportunitiesIcon from 'components/icons/prospect.svg';
import ProductsIcon from 'components/icons/metrics/products.svg';
import MarketsIcon from 'components/icons/metrics/carriers.svg';
import KPIIcon from 'components/icons/pages/kpi.svg';
import ReportsIcon from 'components/icons/pages/report-studio.svg';
import GrowthPageIcon from 'components/icons/pages/growth.svg';
import DefaultIcon from 'components/icons/bookmark.svg';
import O360ScoreIcon from 'components/icons/pages/o360.svg';
import DocumentsIcon from 'components/icons/files/document.svg';
import DocumentsLibraryIcon from 'components/icons/files/file.svg';
import DocumentsCompareIcon from 'components/icons/compare.svg';
import LossRunsIcon from 'components/icons/pages/loss-runs.svg';
import RetentionIcon from 'components/icons/pages/retention.svg';
import CarrierAppetiteIcon from 'components/icons/pages/carrier-appetite.svg';
import MonolineIcon from 'components/icons/monoline.svg';
import WhatsNewIcon from 'components/icons/news.svg';
import HomeIcon from 'components/icons/pages/home.svg';
import SettingsIcon from 'components/icons/settings.svg';
import UploadIcon from 'components/icons/upload.svg';

export enum PageType {
  AI = 'AI',
  Account = 'Account',
  AccountManager = 'AccountManager',
  AccountManagers = 'AccountManagers',
  AccountExecutive = 'AccountExecutive',
  AccountExecutives = 'AccountExecutives',
  Accounts = 'Accounts',
  Activities = 'Activities',
  Activity = 'Activity',
  ActivityEmployee = 'ActivityEmployee',
  CarrierAppetite = 'CarrierAppetite',
  DataManagement = 'DataManagement',
  Default = 'Default',
  Growth = 'Growth',
  Home = 'Home',
  Industries = 'Industries',
  Industry = 'Industry',
  Intermediaries = 'Intermediaries',
  Intermediary = 'Intermediary',
  Kpi = 'KPIs',
  Market = 'Market',
  Markets = 'Markets',
  MonolineRatios = 'MonolineRatios',
  RetailClient = 'RetailClient',
  RetailClients = 'RetailClients',
  O360 = 'O360 Score',
  Producer = 'Producer',
  Producers = 'Producers',
  Product = 'Product',
  Products = 'Products',
  Prospect = 'Prospect',
  ReportStudio = 'ReportStudio',
  Reports = 'Reports',
  Retention = 'Retention',
  Renewals = 'Renewals',
  Unknown = 'Unknown',
  WhatsNew = 'WhatsNew',
  Settings = 'Settings',

  // Workflows
  Workflows = 'Workflows',
  Documents = 'Documents',
  DocumentsLibrary = 'DocumentsLibrary',
  DocumentsCompare = 'DocumentsCompare',
  ProposalGeneration = 'ProposalGeneration',
  SubmissionGeneration = 'SubmissionGeneration',
  SoiGeneration = 'SoiGeneration',
  LossRuns = 'LossRuns',
  AcordPrefill = 'AcordPrefill',
}

export const savedPagesIconsMap: Record<string, SVGIcon> = {
  [PageType.AI]: AiAssistIcon,
  [PageType.Account]: AccountsIcon,
  [PageType.AccountExecutive]: AccountExecutivesIcon,
  [PageType.AccountExecutives]: AccountExecutivesIcon,
  [PageType.AccountManager]: AccountManagersIcon,
  [PageType.AccountManagers]: AccountManagersIcon,
  [PageType.Accounts]: AccountsIcon,
  [PageType.Activities]: ActivitiesIcon,
  [PageType.Activity]: ActivitiesIcon,
  [PageType.ActivityEmployee]: ActivitiesIcon,
  [PageType.CarrierAppetite]: CarrierAppetiteIcon,
  [PageType.DataManagement]: UploadIcon,
  [PageType.Default]: DefaultIcon,
  [PageType.Growth]: GrowthPageIcon,
  [PageType.Home]: HomeIcon,
  [PageType.Industries]: IndustriesIcon,
  [PageType.Industry]: IndustriesIcon,
  [PageType.Intermediaries]: MarketsIcon,
  [PageType.Intermediary]: MarketsIcon,
  [PageType.Kpi]: KPIIcon,
  [PageType.Market]: MarketsIcon,
  [PageType.Markets]: MarketsIcon,
  [PageType.MonolineRatios]: MonolineIcon,
  [PageType.RetailClients]: AccountsIcon,
  [PageType.RetailClient]: AccountsIcon,
  [PageType.O360]: O360ScoreIcon,
  [PageType.Producer]: ProducersIcon,
  [PageType.Producers]: ProducersIcon,
  [PageType.Product]: ProductsIcon,
  [PageType.Products]: ProductsIcon,
  [PageType.Prospect]: OpportunitiesIcon,
  [PageType.ReportStudio]: ReportsIcon,
  [PageType.Reports]: ReportsIcon,
  [PageType.Renewals]: RenewalsIcon,
  [PageType.Retention]: RetentionIcon,
  [PageType.WhatsNew]: WhatsNewIcon,
  [PageType.Settings]: SettingsIcon,
  // Workflows
  [PageType.Workflows]: DocumentsIcon,
  [PageType.Documents]: DocumentsIcon,
  [PageType.DocumentsLibrary]: DocumentsLibraryIcon,
  [PageType.DocumentsCompare]: DocumentsCompareIcon,
  [PageType.LossRuns]: LossRunsIcon,
  [PageType.ProposalGeneration]: DocumentsIcon,
  [PageType.SubmissionGeneration]: DocumentsIcon,
  [PageType.SoiGeneration]: DocumentsIcon,
  [PageType.AcordPrefill]: DocumentsIcon,
};

export const savedPagesSubTitlesMap: Record<string, string> = {
  [PageType.AI]: 'Ennabl AI',
  [PageType.Account]: 'Account',
  [PageType.AccountExecutive]: 'Account Executive',
  [PageType.AccountExecutives]: 'All Account Executives',
  [PageType.AccountManager]: 'Account Manager',
  [PageType.AccountManagers]: 'All Account Managers',
  [PageType.Accounts]: 'All Accounts',
  [PageType.Activities]: 'All Activities',
  [PageType.Activity]: 'Activity',
  [PageType.ActivityEmployee]: 'Activity Employee',
  [PageType.CarrierAppetite]: 'Carrier Appetite',
  [PageType.Default]: 'Page',
  [PageType.Growth]: 'All Growth',
  [PageType.Home]: 'Home',
  [PageType.Industries]: 'All Industries',
  [PageType.Industry]: 'Industry',
  [PageType.Intermediaries]: 'All Intermediaries',
  [PageType.Intermediary]: 'Intermediary',
  [PageType.Kpi]: 'KPIs',
  [PageType.Market]: 'Market',
  [PageType.Markets]: 'All Markets',
  [PageType.MonolineRatios]: 'Monoline Ratios',
  [PageType.RetailClients]: 'All Clients',
  [PageType.RetailClient]: 'Client',
  [PageType.Producer]: 'Producer',
  [PageType.Producers]: 'All Producers',
  [PageType.Product]: 'Product',
  [PageType.Products]: 'All Products',
  [PageType.Prospect]: 'Prospect',
  [PageType.ReportStudio]: 'All Reports',
  [PageType.Reports]: 'Reports',
  [PageType.Renewals]: 'All Renewals',
  [PageType.Retention]: 'Retention',
  [PageType.WhatsNew]: `What's New`,
  [PageType.Settings]: `Settings`,

  // Workflows
  [PageType.Workflows]: 'All Workflows',
  [PageType.Documents]: 'All Documents',
  [PageType.DocumentsLibrary]: 'Documents Library',
  [PageType.DocumentsCompare]: 'Documents Compare',
  [PageType.ProposalGeneration]: 'Proposal Generation',
  [PageType.SubmissionGeneration]: 'Submission Generation',
  [PageType.SoiGeneration]: 'Summary of Insurance',
  [PageType.LossRuns]: 'Loss Runs',
  [PageType.AcordPrefill]: 'ACORD Builder',
};

export const pageTypeToEntityAccessMap: Record<PageType, keyof ReturnType<typeof useEntityAccess>> = {
  [PageType.AI]: 'withEnnablAI',
  [PageType.Account]: 'withAccounts',
  [PageType.AccountManager]: 'withAccountManagers',
  [PageType.AccountManagers]: 'withAccountManagers',
  [PageType.AccountExecutive]: 'withAccountExecutives',
  [PageType.AccountExecutives]: 'withAccountExecutives',
  [PageType.Accounts]: 'withAccounts',
  [PageType.Activities]: 'withActivities',
  [PageType.Activity]: 'withActivities',
  [PageType.ActivityEmployee]: 'withActivities',
  [PageType.CarrierAppetite]: 'withCarrierAppetite',
  [PageType.DataManagement]: 'withDataManagement',
  [PageType.Default]: 'withDecisions',
  [PageType.Documents]: 'withAmsDocuments',
  [PageType.Workflows]: 'withWorkflows',
  [PageType.DocumentsLibrary]: 'withDocumentsLibrary',
  [PageType.DocumentsCompare]: 'withDocumentsCompare',
  [PageType.ProposalGeneration]: 'withProposalGeneration',
  [PageType.SubmissionGeneration]: 'withSubmissionGeneration',
  [PageType.SoiGeneration]: 'withSoiGeneration',
  [PageType.AcordPrefill]: 'withAcordPrefill',
  [PageType.Growth]: 'withGrowth',
  [PageType.Home]: 'withDecisions',
  [PageType.Industries]: 'withIndustries',
  [PageType.Industry]: 'withIndustries',
  [PageType.Intermediaries]: 'withMarkets',
  [PageType.Intermediary]: 'withMarkets',
  [PageType.Kpi]: 'withKpis',
  [PageType.Market]: 'withMarkets',
  [PageType.Markets]: 'withMarkets',
  [PageType.MonolineRatios]: 'withMonolineRatios',
  [PageType.RetailClient]: 'withMga',
  [PageType.RetailClients]: 'withMga',
  [PageType.O360]: 'withO360Score',
  [PageType.Producer]: 'withProducers',
  [PageType.Producers]: 'withProducers',
  [PageType.Product]: 'withProducts',
  [PageType.Products]: 'withProducts',
  [PageType.Prospect]: 'withAccounts',
  [PageType.ReportStudio]: 'withReportsStudio',
  [PageType.Reports]: 'withReportsStudio',
  [PageType.LossRuns]: 'withLossRuns',
  [PageType.Retention]: 'withRetention',
  [PageType.Renewals]: 'withRenewals',
  [PageType.WhatsNew]: 'withWhatsNew',
  [PageType.Settings]: 'withUserManagement',

  [PageType.Unknown]: 'withDecisions',
};
